/**
 * useWorkspaceDocument — loads one document into the two-column workspace shell.
 *
 * Extracted verbatim from `MainLayout`'s `DocumentDetailsLoader` by Lens S5 (2026-08-14) so the
 * review workspace and the read-only Lens view share ONE loader instead of two that drift. The
 * review path's behaviour is unchanged; the only new thing is that claiming is a caller decision.
 *
 * `claimForReview` is the whole difference between the two callers, and it is deliberately not
 * defaulted: the review queue passes `true`, Lens passes `false`. Opening a document through Lens
 * must not claim a review lock — a clerk searching the archive is reading, not taking work, and a
 * silent claim would park somebody else's queue item under their name.
 */

import { useEffect, useRef, useState } from 'react';
import { authFetch } from '../services/authFetch';
import { ExtractedDocumentData } from '../types';

/** The raw stored audit document, as returned by `GET /api/documents/{documentId}`. */
export interface WorkspaceDocumentRecord {
  documentId: string;
  documentType?: string;
  referenceNumber?: string;
  currentStatus?: string;
  fileName?: string;
  createdAt?: string;
  uploadedBy?: string;
  blobStorageUrl?: string;
  extractedFields?: any[];
  validation?: {
    corrections?: {
      fieldId: string;
      originalValue: string;
      correctedValue: string;
      correctionReason?: string;
      timestamp: string;
      correctedBy: string;
    }[];
    validatedBy?: string;
    completedAt?: string;
  };
}

export interface UseWorkspaceDocumentResult {
  /** Shaped for the workspace shell (fields, PDF object URL, metadata). */
  extractedData: ExtractedDocumentData | null;
  /** The stored record, for surfaces that need more than the shell shape (correction history). */
  record: WorkspaceDocumentRecord | null;
  loading: boolean;
  error: string | null;
}

const getFieldType = (fieldName: string): 'text' | 'number' | 'date' | 'currency' => {
  const name = fieldName.toLowerCase();
  if (name.includes('date')) return 'date';
  if (name.includes('amount') || name.includes('price') || name.includes('consideration')) return 'currency';
  if (name.includes('number') && !name.includes('parcel')) return 'number';
  return 'text';
};

export function useWorkspaceDocument(
  documentId: string,
  options: { claimForReview: boolean }
): UseWorkspaceDocumentResult {
  const { claimForReview } = options;
  const [extractedData, setExtractedData] = useState<ExtractedDocumentData | null>(null);
  const [record, setRecord] = useState<WorkspaceDocumentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // `claimForReview` is intentionally NOT a dependency: it is fixed per route (the review route
  // always claims, Lens never does), so re-running the whole load if it ever changed identity
  // would refetch for no reason. Read through a ref so the effect always sees the current value.
  const claimRef = useRef(claimForReview);
  claimRef.current = claimForReview;

  useEffect(() => {
    setExtractedData(null);
    setRecord(null);
    setLoading(true);
    setError(null);

    let cancelled = false;

    const loadDocument = async () => {
      try {
        // Start the PDF download CONCURRENTLY with the metadata fetch.
        //
        // This is only possible because S5 re-addressed the proxy from a caller-supplied `blobUrl`
        // to `documentId`: it no longer needs anything out of the `/documents/{id}` response, so
        // the two round trips (one of them a multi-MB blob) no longer have to run back to back.
        // The server resolves the blob from the document under tenant + scope and answers 404 for
        // a document that is missing, out of scope, or has no stored source file — which is also
        // why no `blobStorageUrl` guard is needed before starting it.
        const pdfPromise = authFetch(
          `/document/content?documentId=${encodeURIComponent(documentId)}`,
          { headers: { 'Accept': 'application/pdf' } }
        ).catch(() => null);

        // Fetch single document by ID (not the full collection). The server filters this by
        // tenant AND the caller's analyzerScope (logic/documentAccess), so an out-of-scope
        // document is a 404 here — indistinguishable from one that does not exist.
        const response = await authFetch(`/documents/${encodeURIComponent(documentId)}`);
        if (!response.ok) {
          if (response.status === 404) {
            setError('Document not found');
          } else {
            setError('Failed to load document');
          }
          return;
        }

        const doc = await response.json();

        // One-editor-per-document: acquire the review lock when a claimable document is opened by
        // direct navigation (deep link, bookmark, or the exception dashboard "open"). The work
        // queue's "Start next"/Open path already claims before it navigates here, but a direct open
        // would otherwise enter the workspace holding NO lock — letting two people edit the same
        // document concurrently. Re-claiming a document the caller already holds just refreshes the
        // lock (idempotent), so the double-claim from the queue path is harmless.
        // Only PENDING_VALIDATION / VALIDATION_IN_PROGRESS are claimable; FAILED, SUBMISSION_PENDING,
        // COMPLETED, VALIDATED and ON_HOLD are opened read-only (claiming ON_HOLD would silently end
        // the hold), so they are never auto-claimed here. A 409 (someone else holds an active lock)
        // is expected and non-fatal — the workspace's review-status poll surfaces the take-over
        // lock-out — so a failed claim never blocks viewing.
        if (
          claimRef.current &&
          (doc?.currentStatus === 'PENDING_VALIDATION' || doc?.currentStatus === 'VALIDATION_IN_PROGRESS')
        ) {
          try {
            await authFetch('/queue/claim', {
              method: 'POST',
              body: JSON.stringify({ documentId }),
            });
          } catch {
            // Best-effort — the workspace's live lock poll and the server's lazy lock expiry are the backstops.
          }
        }

        const transformedData: ExtractedDocumentData = {
          documentId: doc.documentId,
          documentUrl: '',
          totalPages: 1,
          fields: (doc.extractedFields || []).map((field: any, index: number) => ({
            id: `field-${index}`,
            name: field.name,
            value: field.value,
            // Pre-normalization value (UX4 transformation preview) — present only when the
            // normalize pipeline changed the value.
            rawValue: field.rawValue,
            confidence: Math.round(field.confidence * 100),
            required: true,
            fieldType: getFieldType(field.name),
            boundingBoxes: field.boundingBoxes || []
          })),
          metadata: {
            fileName: doc.fileName,
            fileSize: 0,
            uploadDate: doc.createdAt,
            processingDate: doc.createdAt,
            documentType: doc.documentType || 'DEED'
          }
        } as ExtractedDocumentData;

        // Collect the PDF started above. Served through our API proxy rather than direct blob
        // access, because government VPNs/firewalls whitelist our API domain but may block
        // *.blob.core.windows.net.
        //
        // `extractedData` is published ONCE, with the PDF already attached — deliberately not in
        // two phases. The review workspace keys its validation context off this object, so a
        // second publish mid-session would risk resetting an analyst's in-progress work for a
        // cosmetic gain in time-to-first-paint.
        try {
          const pdfResponse = await pdfPromise;
          if (pdfResponse?.ok) {
            const pdfBlob = await pdfResponse.blob();
            const objectUrl = URL.createObjectURL(pdfBlob);
            if (cancelled) {
              URL.revokeObjectURL(objectUrl);
            } else {
              objectUrlRef.current = objectUrl;
              transformedData.documentUrl = objectUrl;
            }
          } else if (pdfResponse) {
            console.error('PDF content proxy failed:', pdfResponse.status);
          }
        } catch (fetchErr) {
          console.error('PDF content fetch failed:', fetchErr);
        }

        if (!cancelled) {
          setRecord(doc as WorkspaceDocumentRecord);
          setExtractedData(transformedData);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load document');
          console.error('Error loading document:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDocument();

    // Cleanup: revoke object URL when document changes or component unmounts
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [documentId]);

  return { extractedData, record, loading, error };
}
